import api from './api';
import { rootStore } from '../_store/index';

// Mock events data for development/fallback
const mockEvents = [
  {
    _id: '1',
    title: 'Community Health Screening',
    description: 'Free health screenings including blood pressure, cholesterol, and diabetes testing. Walk-ins welcome.',
    date: '2025-07-20',
    startDate: '2025-07-20',
    startTime: '09:00',
    endTime: '15:00',
    location: { name: 'Downtown Community Center' },
    capacity: 100,
    status: 'published',
    registrations: [
      { _id: '1', name: 'John Doe', email: 'john@example.com' },
      { _id: '2', name: 'Jane Smith', email: 'jane@example.com' }
    ],
    createdAt: '2025-06-01T10:00:00Z'
  },
  {
    _id: '2',
    title: 'Heart Health Awareness Workshop',
    description: 'Educational workshop covering heart disease prevention, healthy lifestyle choices, and early warning signs.',
    date: '2025-08-15',
    startDate: '2025-08-15',
    startTime: '10:00',
    endTime: '14:00',
    location: { name: 'Medical Plaza' },
    capacity: 50,
    status: 'published',
    registrations: [
      { _id: '3', name: 'Bob Johnson', email: 'bob@example.com' }
    ],
    createdAt: '2025-06-02T14:30:00Z'
  }
];

class EventService {  // Get all events
  async getAllEvents() {
    try {
      const authStore = rootStore?.authStore;

      // Check if user has event_management permission
      if (!authStore?.hasPermission('event_management')) {
        console.log('User does not have event_management permission, using public events endpoint');
        // Use public endpoint for users without event_management permission
        const response = await api.get('/events/public');
        if (response.success && response.data) {
          return {
            success: true,
            data: response.data.events || [],
            pagination: response.data.pagination
          };
        }
        return response;
      }

      // Try admin endpoint first (requires authentication and permission)
      const token = localStorage.getItem('bhaAuthToken');
      if (token) {
        try {
          const response = await api.get('/events');
          // Extract events array from nested response structure
          if (response.success && response.data) {
            return {
              success: true,
              data: response.data.events || [],
              pagination: response.data.pagination
            };
          }
          return response;
        } catch (error) {
          // If admin endpoint fails, fallback to public endpoint
          console.warn('Admin events endpoint failed, falling back to public:', error);
        }
      }
      
      // Use public endpoint as fallback
      const response = await api.get('/events/public');
      // Extract events array from nested response structure
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data.events || [],
          pagination: response.data.pagination
        };
      }
      return response;
    } catch (error) {      console.error('Error fetching events from API, using mock data:', error);
      // Return mock data as fallback with consistent structure
      return { data: mockEvents, success: true };
    }
  }

  // Get single event by ID
  async getEvent(eventId) {
    try {
      const response = await api.get(`/events/${eventId}`);
      return response;
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  }
  // Create new event
  async createEvent(eventData) {
    try {
      const token = localStorage.getItem('bhaAuthToken');
      if (!token) {
        throw new Error('Authentication required. Please log in as an admin to create events.');
      }
      
      const response = await api.post('/events', eventData);
      return response;
    } catch (error) {
      console.error('Error creating event:', error);
      
      // Provide more specific error messages
      if (error.message.includes('401') || error.message.includes('Not Authorized')) {
        throw new Error('Authentication required. Please log in as an admin to create events.');
      } else if (error.message.includes('403') || error.message.includes('Access denied')) {
        throw new Error('Admin privileges required to create events.');
      } else if (error.message.includes('404')) {
        throw new Error('Events API endpoint not found. Please check backend configuration.');
      }
      
      throw error;
    }
  }

  // Update event
  async updateEvent(eventId, eventData) {
    try {
      const response = await api.put(`/events/${eventId}`, eventData);
      return response;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  // Update event status
  async updateEventStatus(eventId, status) {
    try {
      const response = await api.patch(`/events/${eventId}/status`, { status });
      return response;
    } catch (error) {
      console.error('Error updating event status:', error);
      throw error;
    }
  }

  // Delete event
  async deleteEvent(eventId) {
    try {
      const response = await api.delete(`/events/${eventId}`);
      return response;
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }  // Get active events (for public display)
  async getActiveEvents() {
    try {
      const response = await api.get('/events/public');
      // Extract events array from nested response structure
      if (response.success && response.data) {
        const activeEvents = (response.data.events || []).filter(event => event.status === 'active');
        return {
          success: true,
          data: activeEvents,
          pagination: response.data.pagination
        };
      }
      return response;
    } catch (error) {
      console.error('Error fetching active events from API, using mock data:', error);
      // Return filtered mock data as fallback
      const activeEvents = mockEvents.filter(event => event.status === 'active');
      return { data: activeEvents, success: true };
    }
  }

  // Register for event
  async registerForEvent(eventId, registrationData) {
    try {
      const response = await api.post(`/events/${eventId}/register`, registrationData);
      return response;
    } catch (error) {
      console.error('Error registering for event:', error);
      throw error;
    }
  }

  // Get event registrations (admin only)
  async getEventRegistrations(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/registrations`);
      return response;
    } catch (error) {
      console.error('Error fetching event registrations:', error);
      throw error;
    }
  }

  // Get event statistics
  async getEventStats(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/stats`);
      return response;
    } catch (error) {
      console.error('Error fetching event stats:', error);
      throw error;
    }
  }

  // Check in patient to event
  async checkInPatient(eventId, patientId) {
    try {
      const response = await api.post(`/events/${eventId}/checkin`, { patientId });
      return response;
    } catch (error) {
      console.error('Error checking in patient:', error);
      throw error;
    }
  }

  // Get event queue
  async getEventQueue(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/queue`);
      return response;
    } catch (error) {
      console.error('Error fetching event queue:', error);
      throw error;
    }
  }
}

export default new EventService();
